import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Table } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import useOrderTotals from '../../hooks/useOrderTotals';
import usePricingRules from '../../hooks/usePricingRules';
import { formatPrice, taxLabel, renderAmount } from '../../utils/formatPrice';
import colors from '../../constants/colors';
// Import React Icons
import { FaPlusCircle, FaTrash } from 'react-icons/fa';

const OrderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    user_id: '',
    shipping_address: '',
    payment_method: 'multibanco',
    phone: '',
    email: '',
    status: 'pending'
  });
  
  const [orderItems, setOrderItems] = useState([]);
  const [newItem, setNewItem] = useState({ product_id: '', quantity: 1 });
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [validated, setValidated] = useState(false);
  // Numa encomenda já criada as linhas são só-leitura (a API ignora-as no PUT),
  // por isso valem sempre os totais gravados, aos preços da altura.
  const [savedTotals, setSavedTotals] = useState(null);
  const pricingRules = usePricingRules();

  // Define fetchOrderDetails with useCallback
  const fetchOrderDetails = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await axiosInstance.get(`/order/${id}`);
      const orderData = response;
      
      setFormData({
        user_id: orderData.user_id || '',
        shipping_address: orderData.shipping_address || '',
        payment_method: orderData.payment_method || 'multibanco',
        phone: orderData.phone || '',
        email: orderData.email || '',
        status: orderData.status || 'pending'
      });
      
      if (orderData.orderItems && orderData.orderItems.length > 0) {
        setOrderItems(orderData.orderItems);
      }

      setSavedTotals({
        totalPriceWithoutTax: orderData.total_price_without_tax,
        totalTax: orderData.total_tax,
        shippingCost: orderData.shipping_cost,
        totalWithTax: orderData.total_price_with_tax
      });
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Erro ao carregar dados da encomenda.');
    } finally {
      setInitialLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    // Check if user is admin
    if (!user || !user.isAdmin) {
      navigate('/');
      return;
    }
    
    fetchProducts();
    fetchUsers();
    
    // If in edit mode, fetch order data
    if (isEditMode) {
      fetchOrderDetails();
    } else {
      setInitialLoading(false);
    }
  }, [id, isEditMode, user, navigate, fetchOrderDetails]);
  
  const fetchProducts = async () => {
    try {
      const response = await axiosInstance.get('/product');
      setProducts(response.rows);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };
  
  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/user');
      setUsers(response);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };
  
  // A API identifica cada linha pelo SKU da variante ou, na sua ausência, pelo id do produto.
  // Estas são as linhas enviadas TANTO para o cálculo de totais COMO para a encomenda:
  // os ids têm de ser os mesmos, senão os totais declarados não batem com os recalculados.
  const orderLines = useMemo(
    () =>
      orderItems.map(item => ({
        product_id: (item.variant && item.variant.sku) || item.product_id,
        quantity: item.quantity
      })),
    [orderItems]
  );

  // Numa encomenda existente não se pede recálculo: as linhas não mudam.
  const {
    orderTotals: freshTotals,
    loading: totalsLoading,
    error: totalsError
  } = useOrderTotals(isEditMode ? [] : orderLines);

  // A percentagem do IVA não é gravada com a encomenda; mostra-se a da API.
  const orderTotals = isEditMode
    ? savedTotals && { ...savedTotals, taxRate: pricingRules?.taxRate }
    : freshTotals;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prev => ({ ...prev, [name]: value }));
  };
  
  const addOrderItem = () => {
    if (!newItem.product_id || newItem.quantity < 1) {
      return;
    }

    // Check if product already exists in order items
    const existingItemIndex = orderItems.findIndex(item => item.product_id === parseInt(newItem.product_id));
    
    if (existingItemIndex !== -1) {
      // Update existing item quantity
      const updatedItems = [...orderItems];
      updatedItems[existingItemIndex].quantity += parseInt(newItem.quantity);
      setOrderItems(updatedItems);
    } else {
      // Add new item
      const product = products.find(p => p.id === parseInt(newItem.product_id));
      setOrderItems([
        ...orderItems,
        {
          product_id: parseInt(newItem.product_id),
          quantity: parseInt(newItem.quantity),
          price: product ? product.price : 0
        }
      ]);
    }
    
    // Reset new item form
    setNewItem({ product_id: '', quantity: 1 });
  };
  
  const removeOrderItem = (index) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    if (!form.checkValidity() || orderItems.length === 0) {
      e.stopPropagation();
      setValidated(true);
      if (orderItems.length === 0) {
        setError('Por favor adicione pelo menos um produto à encomenda.');
      }
      return;
    }
    
    if (!orderTotals) {
      setError('Os totais ainda não foram calculados. Aguarde um momento e tente novamente.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Sem alterações às linhas reenviam-se os totais gravados, para não
      // sobrepor o valor efetivamente cobrado com os preços de hoje.
      const orderData = {
        ...formData,
        items: orderLines,
        total_price_without_tax: orderTotals.totalPriceWithoutTax,
        total_tax: orderTotals.totalTax,
        total_price_with_tax: orderTotals.totalWithTax,
        shipping_cost: orderTotals.shippingCost
      };

      if (isEditMode) {
        await axiosInstance.put(`/order/${id}`, orderData);
      } else {
        await axiosInstance.post('/order/createOrder', orderData);
      }
      
      navigate('/admin-dashboard');
    } catch (err) {
      console.error('Error saving order:', err);
      setError(
        err.response?.data?.message || 
        'Erro ao guardar encomenda. Por favor verifique os dados e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  const amount = (value) => renderAmount(value, { loading: totalsLoading, totals: orderTotals });

  if (initialLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">A carregar...</span>
        </Spinner>
      </Container>
    );
  }
  
  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="shadow-sm">
            <Card.Header as="h5">
              {isEditMode ? 'Editar Encomenda' : 'Criar Nova Encomenda'}
            </Card.Header>
            <Card.Body>
              {(error || totalsError) && (
                <Alert variant="danger">{error || totalsError}</Alert>
              )}
              
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formUserId">
                      <Form.Label>Cliente</Form.Label>
                      <Form.Select
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Selecione um cliente</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.username} ({user.email})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        Por favor selecione um cliente.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPaymentMethod">
                      <Form.Label>Método de Pagamento</Form.Label>
                      <Form.Select
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      >
                        <option value="multibanco">Multibanco</option>
                        <option value="mbway">MB WAY</option>
                        <option value="credit_card">Cartão de Crédito</option>
                        <option value="paypal">PayPal</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3" controlId="formShippingAddress">
                      <Form.Label>Morada de Entrega</Form.Label>
                      <Form.Control
                        type="text"
                        name="shipping_address"
                        value={formData.shipping_address}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insira uma morada válida.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="formPhone">
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insira um número de telefone.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-4" controlId="formEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insira um email válido.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-4" controlId="formStatus">
                  <Form.Label>Estado</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="pending">Pendente</option>
                    <option value="processing">Em Processamento</option>
                    <option value="shipped">Enviada</option>
                    <option value="delivered">Entregue</option>
                    <option value="canceled">Cancelada</option>
                  </Form.Select>
                </Form.Group>
                
                <h5 className="mt-4 mb-3">Itens da Encomenda</h5>
                
                {/* A API não altera as linhas de uma encomenda já criada,
                    por isso em edição não se prometem alterações que não guarda. */}
                {isEditMode ? (
                  <Alert variant="secondary">
                    As linhas de uma encomenda já criada não podem ser alteradas aqui.
                  </Alert>
                ) : (
                <div className="mb-4 p-3" style={{ backgroundColor: colors.light, borderRadius: '0.25rem' }}>
                  <Row className="align-items-end">
                    <Col md={6}>
                      <Form.Group controlId="formProductId">
                        <Form.Label>Produto</Form.Label>
                        <Form.Select
                          name="product_id"
                          value={newItem.product_id}
                          onChange={handleNewItemChange}
                        >
                          <option value="">Selecione um produto</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} - {formatPrice(product.price)}€
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Group controlId="formQuantity">
                        <Form.Label>Quantidade</Form.Label>
                        <Form.Control
                          type="number"
                          name="quantity"
                          min="1"
                          value={newItem.quantity}
                          onChange={handleNewItemChange}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={3} className="mt-3 mt-md-0">
                      <Button
                        variant="success"
                        onClick={addOrderItem}
                        disabled={!newItem.product_id}
                      >
                        <FaPlusCircle className="me-2" />
                        Adicionar
                      </Button>
                    </Col>
                  </Row>
                </div>
                )}

                {orderItems.length > 0 ? (
                  <div className="table-responsive mb-4">
                    <Table striped bordered>
                      <thead>
                        <tr>
                          <th>Imagem</th>
                          <th>Item</th>
                          <th>Preço Unitário</th>
                          <th>Quantidade</th>
                          <th>Subtotal</th>
                          {!isEditMode && <th>Ações</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item, index) => {
                          const isExistingItem = !!item.product;
                          const productDetails = isExistingItem ? item.product : products.find(p => p.id === item.product_id);
                          const variantDetails = isExistingItem ? item.variant : null;

                          let itemName = `Produto #${item.product_id}`;
                          let itemImageSrc = '/placeholder-image.jpg';
                          let viewProductLink = null;

                          if (variantDetails && variantDetails.product) {
                            itemName = `${variantDetails.product.name || 'Produto'} - ${variantDetails.name}`;
                            if (variantDetails.product.images && variantDetails.product.images.length > 0 && variantDetails.product.images[0]) {
                              itemImageSrc = variantDetails.product.images[0];
                            }
                            viewProductLink = variantDetails.product.id ? `/produtos/${variantDetails.product.id}` : null;
                          } else if (productDetails) {
                            itemName = productDetails.name;
                            if (productDetails.images && productDetails.images.length > 0 && productDetails.images[0]) {
                              itemImageSrc = productDetails.images[0];
                            }
                            viewProductLink = productDetails.id ? `/produtos/${productDetails.id}` : null;
                          }

                          return (
                            <tr key={item.id || item.product_id || index}>
                              <td>
                                <img 
                                  src={itemImageSrc} 
                                  alt={itemName} 
                                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                              </td>
                              <td>
                                {itemName}
                                {variantDetails && (
                                    <small className="d-block text-muted">SKU: {variantDetails.sku || 'N/A'}</small>
                                )}
                                {viewProductLink && (
                                    <small className="d-block">
                                        <button onClick={() => navigate(viewProductLink)} className="btn btn-sm btn-link p-0">
                                          Ver Produto Base
                                        </button>
                                    </small>
                                )}
                              </td>
                              <td>{formatPrice(item.price)}€</td>
                              <td>{item.quantity}</td>
                              <td>{formatPrice(item.price * item.quantity)}€</td>
                              {!isEditMode && (
                                <td>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => removeOrderItem(index)}
                                  >
                                    <FaTrash />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {[
                          ['Soma de Produtos (sem IVA):', orderTotals?.totalPriceWithoutTax],
                          ['Envio:', orderTotals?.shippingCost],
                          [`${taxLabel(orderTotals)}:`, orderTotals?.totalTax],
                          ['Total a Pagar:', orderTotals?.totalWithTax],
                        ].map(([label, value]) => (
                          <tr key={label}>
                            <td colSpan="4" className="text-end"><strong>{label}</strong></td>
                            <td><strong>{amount(value)}</strong></td>
                            {!isEditMode && <td></td>}
                          </tr>
                        ))}
                      </tfoot>
                    </Table>
                  </div>
                ) : (
                  <Alert variant="warning">
                    Nenhum item adicionado à encomenda.
                  </Alert>
                )}
                
                <div className="d-flex justify-content-between mt-4">
                  <Button 
                    variant="secondary" 
                    as={Link} 
                    to="/admin-dashboard"
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit"
                    disabled={loading || totalsLoading || !orderTotals || orderItems.length === 0}
                    style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}
                  >
                    {loading ? 'A guardar...' : isEditMode ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderForm;
